import ContextMenu from "components/DesignSystem/ContextMenu/ContextMenu";
import { AddFileIcon } from "components/icons/AddFile";
import { UploadFileIcon } from "components/icons/uploadFile";
import {
  FILE_UPLOADING_MESSAGE,
  FILE_UPLOAD_MESSAGE,
  NEW_FOLDER_MESSAGE,
} from "constants/documents";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import React, { ReactNode, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useParams } from "react-router-dom";
import {
  useCreateFolderMutation,
  useUploadFileMutation,
} from "store/apis/documents";
import { BackendError } from "types/utils/error";
import { AddNewFolder } from "./documentsModal/AddNewFolder";
import { useRoleBasedView } from "hooks/useRoleBasedView";

const RightClickMenu = ({
  children,
  refetch,
  isInsidePrivateFolder,
}: {
  children: ReactNode;
  refetch: () => void;
  isInsidePrivateFolder: boolean;
}) => {
  const query = useQuery();
  const searchTerm = query.get("search");
  const { alertToast, successToast, promise: promiseToast } = useToast();
  const [uploadFiles] = useUploadFileMutation();
  const [createFolder] = useCreateFolderMutation();
  const { uuid: groupId } = useCurrentGroupContext();
  const { docId } = useParams<{ docId: string }>();
  const [isPrivate, setIsPrivate] = useState(false);
  const { isAdmin } = useRoleBasedView();

  const onDrop = async (acceptedFiles: File[]) => {
    promiseToast(
      () =>
        uploadFiles({
          files: acceptedFiles,
          groupId,
          parentFolderId: docId,
        }).unwrap(),
      {
        loading: FILE_UPLOADING_MESSAGE,
        success: () => {
          closeModal();
          refetch();
          return FILE_UPLOAD_MESSAGE;
        },
        error: (error) => {
          return (error as BackendError).data?.error?.message;
        },
      }
    );
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
  });

  const { isOpen, close, open: openModal } = useModal();

  const closeModal = () => {
    setIsPrivate(false);
    close();
  };

  const onCreateFolder = async ({ folder_name }: { folder_name: string }) => {
    try {
      await createFolder({
        folder_name,
        groupId,
        parentFolderId: docId,
        is_private: isPrivate,
      }).unwrap();
      successToast({ message: NEW_FOLDER_MESSAGE });
      closeModal();
      refetch();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const openPrivate = () => {
    setIsPrivate(true);
    openModal();
  };

  return (
    <>
      <ContextMenu.Root modal={false}>
        <ContextMenu.Trigger disabled={Boolean(searchTerm)}>
          {children}
        </ContextMenu.Trigger>
        <ContextMenu.Portal>
          <ContextMenu.Content>
            <ContextMenu.Item
              className="hover:t-text-purple data-[disabled]:t-text-neutral-30"
              onSelect={open}
            >
              <div
                {...getRootProps()}
                className="t-flex t-items-center t-gap-2"
              >
                <UploadFileIcon color="currentColor" />
                Upload File
              </div>
            </ContextMenu.Item>
            {isAdmin && (
              <ContextMenu.Item
                className="t-flex t-items-center t-gap-2 hover:t-text-purple data-[disabled]:t-text-neutral-30"
                onSelect={openPrivate}
              >
                <AddFileIcon color="currentColor" />
                Private Folder
              </ContextMenu.Item>
            )}
            {!isInsidePrivateFolder && (
              <ContextMenu.Item
                className="t-flex t-items-center t-gap-2 hover:t-text-purple data-[disabled]:t-text-neutral-30"
                onSelect={openModal}
              >
                <AddFileIcon color="currentColor" />
                New Folder
              </ContextMenu.Item>
            )}
          </ContextMenu.Content>
        </ContextMenu.Portal>
      </ContextMenu.Root>

      <input {...getInputProps()} />
      <AddNewFolder
        isOpen={isOpen}
        close={closeModal}
        createFolder={onCreateFolder}
        isPrivate={isPrivate}
      />
    </>
  );
};

export default RightClickMenu;
